import '@app/index.css'
import {config as i18nextConfig} from '@app/translations/index'
import {CssBaseline} from '@mui/material'
import i18n from 'i18next'
import type {FC, ReactElement} from 'react'
import {I18nextProvider} from 'react-i18next'
import {Provider} from 'react-redux'
import {BrowserRouter} from 'react-router-dom'
import ErrorBoundary from '@shared/components/ErrorBoundary'
import NewVersionAvailableBanner from '@shared/components/NewVersionAvailableBanner'
import {AuthProvider} from './AuthProvider'
import GlobalUnsavedChangesGuard from './GlobalUnsavedChangesGuard'
import MBAISnackBarProvider from './MBAISnackBarProvider'
import Router from './Router'
import {setupStore} from './store'
import MBAIThemeProvider from './themes/MBAIThemeProvider'
import {UserProvider} from './UserProvider'
import {WebSocketNotificationsProvider} from './WebSocketNotificationsProvider'
import {isDashboardPrintPath} from '@shared/utils/printRoute'

const store = setupStore({})
// Cypress reads the rendered app's store (e.g. to wait until a query has
// settled), so expose this instance, the one the Provider below uses.
if ((window as Window & {Cypress?: unknown}).Cypress) {
  ;(window as Window & {store?: typeof store}).store = store
}
i18n.init(i18nextConfig)

const App: FC = (): ReactElement => (
  <Provider store={store}>
    <MBAISnackBarProvider>
      <MBAIThemeProvider>
        <CssBaseline />
        <BrowserRouter>
          <I18nextProvider i18n={i18n}>
            <AuthProvider>
              <UserProvider>
                <WebSocketNotificationsProvider>
                  {/* Not on the dashboard print route (AB#6645). */}
                  {!isDashboardPrintPath() && <NewVersionAvailableBanner />}
                  <GlobalUnsavedChangesGuard />
                  <ErrorBoundary>
                    <Router />
                  </ErrorBoundary>
                </WebSocketNotificationsProvider>
              </UserProvider>
            </AuthProvider>
          </I18nextProvider>
        </BrowserRouter>
      </MBAIThemeProvider>
    </MBAISnackBarProvider>
  </Provider>
)

export default App
